import {
    CandidateFeedback,
    CandidateJobInterviews,
    MobilePage,
    MobilePageActions,
    MobilePageSection,
    StatusLabel,
} from "@relode/core";
import { JobCandidateStatus, UserRole } from "@relode/core/domain";
import * as React from "react";

import {
    JobManagerContactDetails,
    CandidateProfile,
    CandidateHighlights,
    CandidateHiredInfo,
    CandidateJobDetailsMobile,
    CandidateResumeMobile,
    InterviewAvailability,
} from "../../../../../shared/components/candidate";
import { useCandidateContext } from "../../../_infra";
import { CandidateActionOptions, CandidateActions } from "../candidateActions";
import { CandidateDetailsProps } from "./candidateDetailsProps";

export const CandidateDetailsMobile = (props: CandidateDetailsProps) => {
    const {
        state: {
            data: { candidate, currentLeadId },
            ui: { isCandidateLoading },
        },
    } = useCandidateContext();

    const canViewInterviews =
        candidate.status === JobCandidateStatus.Interviewing ||
        (candidate.upcomingInterviews &&
            candidate.upcomingInterviews.length > 0) ||
        (candidate.pastInterviews && candidate.pastInterviews.length > 0);

    const {
        backUrl,
        addOrUpdateFeedback,
        toggleMessageComposer,
        toggleShareCandidate,
        handleSubmitInterview,
        handleSendManualInterviewNotification,
    } = props;

    const renderFooter = (selectedSection: string): React.ReactNode =>
        candidate.hasMobileActions ? (
            <CandidateActions
                addOrUpdateFeedback={addOrUpdateFeedback}
                feedbackDetails={candidate.feedbackDetails}
            />
        ) : null;

    return (
        <MobilePage
            pageTitle={candidate.highlights?.fullName}
            showTabs={true}
            defaultSection="highlights"
            isBusy={isCandidateLoading}
            actions={
                <MobilePageActions
                    backUrl={backUrl ? backUrl : "/candidates"}
                    title={
                        candidate.highlights
                            ? candidate.highlights.fullName
                            : "n/a"
                    }
                    statusLabel={
                        candidate.status ? (
                            <StatusLabel
                                title={candidate.status.description}
                                jobCandidateStatus={candidate.status}
                            />
                        ) : null
                    }
                >
                    {" "}
                    <CandidateActionOptions
                        shareAction={toggleShareCandidate}
                        messageAction={toggleMessageComposer}
                    />
                </MobilePageActions>
            }
            footerActions={renderFooter}
        >
            <MobilePageSection label="highlights" id="highlights">
                <CandidateJobDetailsMobile
                    job={candidate.job}
                    currentLeadId={currentLeadId}
                    highlights={candidate.highlights}
                />
                <CandidateProfile
                    highlights={candidate.highlights}
                    additionalInfo={candidate.additionalInfo}
                />
                <CandidateHighlights
                    highlights={candidate.highlights}
                    qualifications={candidate.qualifications}
                    additionalInfo={candidate.additionalInfo}
                />
                <InterviewAvailability
                    availability={candidate.availability}
                    additionalInfo={candidate.additionalInfo}
                />
                <JobManagerContactDetails
                    title="questions?"
                    jobManagerContact={candidate.jobManager}
                    leadId={candidate.highlights.leadId}
                    leadName={candidate.highlights.fullName}
                    jobId={candidate.job.jobId}
                    jobName={candidate.job.jobName}
                    contactActionVariant="button"
                />
            </MobilePageSection>

            <MobilePageSection label="resume" id="resume">
                <CandidateResumeMobile resume={candidate.formattedResume} />
            </MobilePageSection>

            <MobilePageSection label="feedback" id="feedback">
                <CandidateFeedback
                    candidateFullName={candidate.highlights.fullName}
                    feedbackDetails={candidate.feedbackDetails}
                    addOrUpdateFeedback={addOrUpdateFeedback}
                />
            </MobilePageSection>
            {canViewInterviews ? (
                <MobilePageSection label="interviews" id="interviews">
                    <CandidateJobInterviews
                        candidateUserId={candidate.highlights.leadId}
                        candidateRoleName={"Candidate"}
                        candidateFullName={candidate.highlights.fullName}
                        candidateFirstName={candidate.highlights.firstName}
                        jobName={candidate.job.jobName}
                        jobId={candidate.job.jobId}
                        companyName={candidate.job.jobName}
                        companyBackground={candidate.job.teamBackground}
                        jobJobManager={{
                            name: candidate.jobManager.fullName,
                            role: UserRole.fromName(
                                candidate.jobManager.roleName
                            ),
                            email: candidate.jobManager.email,
                            userId: candidate.jobManager.userId,
                            phoneNumber: candidate.jobManager.phoneNumber,
                        }}
                        upcomingInterviews={candidate.upcomingInterviews}
                        pastInterviews={candidate.pastInterviews}
                        canEditInterview={
                            candidate.canViewCandidateActions &&
                            candidate.status.id ==
                                JobCandidateStatus.Interviewing.id
                        }
                        canAddInterview={candidate.canAddInterview}
                        isCandidateLoading={isCandidateLoading}
                        onSubmitInterview={handleSubmitInterview}
                        handleSendManualInterviewNotification={
                            handleSendManualInterviewNotification
                        }
                    />
                </MobilePageSection>
            ) : null}
            {candidate?.hiredDetails?.startDate ? (
                <MobilePageSection label="hire info" id="hireInfo">
                    <CandidateHiredInfo
                        hiredInfo={candidate.hiredDetails}
                        candidateJobType={candidate.job.jobType}
                        candidate={candidate}
                    />
                </MobilePageSection>
            ) : null}
        </MobilePage>
    );
};
